import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Canvas } from "@react-three/fiber";
import { Physics } from "@react-three/rapier";
import { Sky, Loader } from "@react-three/drei";
import * as THREE from "three";
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  Chip,
  Stack,
  Button,
  Fade,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import GitHubIcon from "@mui/icons-material/GitHub";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import Car from "../components/world/Car";
import Character from "../components/world/Character";
import Ground from "../components/world/Ground";
import Scenery from "../components/world/Scenery";
import Waterfall from "../components/world/Waterfall";
import Wildlife from "../components/world/Wildlife";
import ProjectMarker from "../components/world/ProjectMarker";
import CaveEntrance from "../components/world/CaveEntrance";
import TouchControls from "../components/world/TouchControls";
import {
  attachKeyboardControls,
  attachMouseControls,
  mouseState,
  setInvertY,
  resetControls,
} from "../components/world/controls";
import { isAudioMuted, setAudioMuted } from "../components/world/audio";
import { projects } from "../data/projects";
import { Project } from "../types";
import { updateMetaTags } from "../utils/seo";

const ROUTINEX_TRAILER_URL = "https://www.youtube.com/watch?v=Cw0YK7_7LAY";

const markerLayout: { position: [number, number, number]; facingAngle: number }[] = (() => {
  const radius = 22;
  return projects.map((_, i) => {
    // Offset so the first project sits directly ahead of the spawn heading
    const angle = (i / projects.length) * Math.PI * 2 - Math.PI / 2;
    const position: [number, number, number] = [
      Math.cos(angle) * radius,
      0.2,
      Math.sin(angle) * radius,
    ];
    // Face the frame toward the world origin so it reads face-on when approached from spawn
    const facingAngle = Math.atan2(-Math.cos(angle), -Math.sin(angle));
    return { position, facingAngle };
  });
})();

const markerPositions = markerLayout.map((m) => m.position);
// Corner of the walkable square (walls at ±40), clear of all marker approach corridors
const WATERFALL_POSITION: [number, number, number] = [30, 0, -30];

interface CaveDef {
  id: string;
  position: [number, number, number];
  label: string;
  accent: string;
  path: string;
}

// Tucked into the far corners of the boundary wall (walls at ±40)
const CAVES: CaveDef[] = [
  { id: "blogs", position: [-34, 0, -34], label: "Blog Cave", accent: "#91bd3a", path: "/blogs" },
  { id: "news", position: [-34, 0, 34], label: "Hacker News Cave", accent: "#ff6600", path: "/news" },
  {
    id: "utilities",
    position: [34, 0, 34],
    label: "Utilities Cave",
    accent: "#2196f3",
    path: "/developer-formatter-toolkit",
  },
];

const caveFacingAngle = (pos: [number, number, number]) =>
  Math.atan2(-pos[0], -pos[2]);

const avoidPositions: [number, number, number][] = [
  ...markerPositions,
  WATERFALL_POSITION,
  ...CAVES.map((c) => c.position),
];

type Mode = "human" | "car";

const readRespawnPosition = (): [number, number, number] => {
  try {
    const stored = sessionStorage.getItem("world_respawn_position");
    if (stored) {
      sessionStorage.removeItem("world_respawn_position");
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length === 3) return parsed as [number, number, number];
    }
  } catch {
    /* fall through to default spawn */
  }
  return [0, 1.2, -16];
};

const World: React.FC = () => {
  const navigate = useNavigate();
  const [activeProject, setActiveProject] = useState<Project | null>(null);
  const [showIntro, setShowIntro] = useState(true);
  const [mode, setMode] = useState<Mode>("human");
  const [mouseLocked, setMouseLocked] = useState(false);
  const [invertY, setInvertYState] = useState(mouseState.invertY);
  const [muted, setMuted] = useState(isAudioMuted());
  const lastEnteredRef = useRef<string | null>(null);
  // Spawn just outside the RoutineX compound gate by default, unless returning
  // from a cave's destination page via its "Back to World" button.
  const lastPositionRef = useRef<[number, number, number]>(readRespawnPosition());
  const lastCaveEnteredRef = useRef<string | null>(null);
  const mouseDetachRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    updateMetaTags({
      title: "Shashi Sharma - Interactive 3D Portfolio",
      description:
        "Walk or drive through an interactive 3D portfolio to explore my projects, powered by Three.js.",
      keywords: "3D portfolio, three.js, interactive, projects, Shashi Sharma",
      canonical: `${window.location.origin}/`,
      ogTitle: "Shashi Sharma - Interactive 3D Portfolio",
      ogDescription: "Walk or drive through an interactive 3D world to explore my projects.",
      ogUrl: `${window.location.origin}/`,
      ogType: "website",
      twitterCard: "summary",
      twitterTitle: "Shashi Sharma - Interactive 3D Portfolio",
      twitterDescription: "Walk or drive through an interactive 3D world to explore my projects.",
    });

    // A key held down when navigating away (e.g. clicking a button instead of
    // releasing it) can leave input "stuck on" since the keyup never fires while
    // this listener is detached — clear the slate before attaching fresh.
    resetControls();
    const detachKeyboard = attachKeyboardControls();
    return () => {
      detachKeyboard();
      mouseDetachRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (!showIntro) return;
    const timer = setTimeout(() => setShowIntro(false), 5000);
    return () => clearTimeout(timer);
  }, [showIntro]);

  // Poll pointer-lock state to drive the "click to enable mouse-look" hint
  useEffect(() => {
    const id = setInterval(() => setMouseLocked(mouseState.pointerLocked), 300);
    return () => clearInterval(id);
  }, []);

  const handleCanvasCreated = useCallback((state: { gl: THREE.WebGLRenderer }) => {
    mouseDetachRef.current?.();
    mouseDetachRef.current = attachMouseControls(state.gl.domElement);
  }, []);

  const handleEnter = (project: Project) => {
    if (lastEnteredRef.current === project.title) return;
    lastEnteredRef.current = project.title;
    setActiveProject(project);
  };

  const handleExit = (project: Project) => {
    if (lastEnteredRef.current === project.title) {
      lastEnteredRef.current = null;
      setActiveProject((current) => (current?.title === project.title ? null : current));
    }
  };

  const handleClose = () => {
    setActiveProject(null);
    lastEnteredRef.current = null;
  };

  const handleModeChange = (_: React.MouseEvent, next: Mode | null) => {
    if (next) setMode(next);
  };

  const handleToggleInvertY = () => {
    const next = !invertY;
    setInvertY(next);
    setInvertYState(next);
  };

  const handleToggleMute = () => {
    const next = !muted;
    setAudioMuted(next);
    setMuted(next);
  };

  const handleEnterCave = (cave: CaveDef) => {
    if (lastCaveEnteredRef.current === cave.id) return;
    lastCaveEnteredRef.current = cave.id;
    // Remember a safe respawn spot (just back from the cave mouth, toward the
    // origin) so the "Back to World" button on the destination page can drop
    // the player right back where they left, not always at default spawn.
    const [cx, , cz] = cave.position;
    const dist = Math.hypot(cx, cz) || 1;
    const respawn: [number, number, number] = [
      cx - (cx / dist) * 6,
      1.2,
      cz - (cz / dist) * 6,
    ];
    try {
      sessionStorage.setItem("world_respawn_position", JSON.stringify(respawn));
    } catch {
      /* ignore persistence failures */
    }
    navigate(cave.path);
  };

  const markers = useMemo(
    () =>
      projects.map((project, i) => {
        const featured = !!project.featured;
        return (
          <ProjectMarker
            key={project.title}
            project={project}
            position={markerLayout[i].position}
            facingAngle={markerLayout[i].facingAngle}
            featured={featured}
            onEnter={handleEnter}
            onExit={handleExit}
          />
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const galleryUrls = activeProject?.screenshots ?? [];

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100vh", overflow: "hidden" }}>
      <Canvas shadows camera={{ position: [0, 4, 9], fov: 55 }} onCreated={handleCanvasCreated}>
        <Sky sunPosition={[100, 40, 100]} turbidity={2} rayleigh={0.6} />
        <ambientLight intensity={0.65} />
        <directionalLight
          position={[15, 20, 10]}
          intensity={1.1}
          castShadow
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
          shadow-camera-left={-30}
          shadow-camera-right={30}
          shadow-camera-top={30}
          shadow-camera-bottom={-30}
        />
        <fog attach="fog" args={["#e8f0dc", 30, 70]} />

        <Suspense fallback={null}>
          <Physics gravity={[0, -18, 0]}>
            <Ground />
            {mode === "human" ? (
              <Character
                spawnPosition={[lastPositionRef.current[0], 1.2, lastPositionRef.current[2]]}
                onPositionChange={(p) => {
                  lastPositionRef.current = [p.x, p.y, p.z];
                }}
              />
            ) : (
              <Car
                spawnPosition={[lastPositionRef.current[0], 1, lastPositionRef.current[2]]}
                onPositionChange={(p) => {
                  lastPositionRef.current = [p.x, p.y, p.z];
                }}
              />
            )}
            <Scenery markerPositions={avoidPositions} />
            <Waterfall position={WATERFALL_POSITION} />
            <Wildlife avoidPositions={avoidPositions} />
            {markers}
            {CAVES.map((cave) => (
              <CaveEntrance
                key={cave.id}
                position={cave.position}
                facingAngle={caveFacingAngle(cave.position)}
                label={cave.label}
                accent={cave.accent}
                onEnter={() => handleEnterCave(cave)}
              />
            ))}
          </Physics>
        </Suspense>
      </Canvas>
      <Loader
        containerStyles={{ background: "#ffffff" }}
        barStyles={{ background: "#91bd3a" }}
        dataStyles={{ color: "#212121", fontFamily: "monospace" }}
      />

      {/* Back button + mode toggle */}
      <Box sx={{ position: "fixed", top: 16, left: 16, zIndex: 30, display: "flex", gap: 1.5 }}>
        <Button
          component={RouterLink}
          to="/blogs"
          startIcon={<ArrowBackIcon />}
          variant="contained"
          sx={{
            backgroundColor: "rgba(255,255,255,0.92)",
            color: "text.primary",
            "&:hover": { backgroundColor: "#ffffff" },
          }}
        >
          Exit to Website
        </Button>

        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={handleModeChange}
          size="small"
          sx={{
            backgroundColor: "rgba(255,255,255,0.92)",
            "& .MuiToggleButton-root": { px: 1.5 },
          }}
        >
          <ToggleButton value="human">
            <DirectionsWalkIcon fontSize="small" sx={{ mr: 0.5 }} />
            Human
          </ToggleButton>
          <ToggleButton value="car">
            <DirectionsCarIcon fontSize="small" sx={{ mr: 0.5 }} />
            Car
          </ToggleButton>
        </ToggleButtonGroup>

        <Tooltip title={`Invert mouse look: ${invertY ? "On" : "Off"} (click to toggle)`}>
          <IconButton
            onClick={handleToggleInvertY}
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              backgroundColor: invertY ? "primary.main" : "rgba(255,255,255,0.92)",
              color: invertY ? "white" : "text.primary",
              "&:hover": {
                backgroundColor: invertY ? "primary.dark" : "#ffffff",
              },
            }}
          >
            <SwapVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={`Sound: ${muted ? "Off" : "On"} (click to toggle)`}>
          <IconButton
            onClick={handleToggleMute}
            sx={{
              backgroundColor: muted ? "rgba(255,255,255,0.92)" : "primary.main",
              color: muted ? "text.primary" : "white",
              "&:hover": {
                backgroundColor: muted ? "#ffffff" : "primary.dark",
              },
            }}
          >
            {muted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Intro / controls hint — desktop only */}
      <Fade in={showIntro || !mouseLocked}>
        <Box
          sx={{
            display: { xs: "none", sm: "block" },
            position: "fixed",
            top: 16,
            right: 16,
            zIndex: 30,
            backgroundColor: "rgba(255,255,255,0.92)",
            borderRadius: 2,
            px: 2.5,
            py: 1.5,
            maxWidth: 280,
            boxShadow: 3,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {mouseLocked ? "Exploring!" : "Click anywhere to look around"}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            Move the mouse to look around. Hold Right-Click (or W) to move
            forward. A/D to turn, Space to jump. Walk or drive into a marker
            to open a project, or into a cave mouth to visit that part of the
            site.
          </Typography>
        </Box>
      </Fade>

      <TouchControls />

      {/* Project detail dialog */}
      <Dialog open={!!activeProject} onClose={handleClose} maxWidth="sm" fullWidth>
        {activeProject && (
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {activeProject.title}
              </Typography>
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            {galleryUrls.length > 1 ? (
              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 2, overflowX: "auto", pb: 0.5 }}
              >
                {galleryUrls.map((src) => (
                  <Box
                    key={src}
                    component="img"
                    src={src}
                    alt={activeProject.title}
                    sx={{
                      height: 260,
                      borderRadius: 2,
                      flexShrink: 0,
                    }}
                  />
                ))}
              </Stack>
            ) : (
              galleryUrls.length === 1 && (
                <Box
                  component="img"
                  src={galleryUrls[0]}
                  alt={activeProject.title}
                  sx={{
                    width: "100%",
                    maxHeight: 260,
                    objectFit: "cover",
                    borderRadius: 2,
                    mb: 2,
                  }}
                />
              )
            )}

            <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
              {activeProject.description}
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
              {activeProject.techStack.map((tech) => (
                <Chip key={tech.name} label={tech.name} size="small" />
              ))}
            </Stack>

            <Stack direction="row" spacing={1.5}>
              {activeProject.liveUrl && (
                <Button
                  component="a"
                  href={activeProject.liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  startIcon={<OpenInNewIcon />}
                >
                  Live
                </Button>
              )}
              {activeProject.git && (
                <Button
                  component="a"
                  href={activeProject.git}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  startIcon={<GitHubIcon />}
                >
                  Source
                </Button>
              )}
              {activeProject.title.includes("RoutineX") && (
                <Button
                  component="a"
                  href={ROUTINEX_TRAILER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  color="error"
                >
                  ▶ Trailer
                </Button>
              )}
            </Stack>
          </DialogContent>
        )}
      </Dialog>
    </Box>
  );
};

export default World;
